import { ReactNode } from 'react';
import { Add, Check, Close, Delete, DoneAll, Remove, Replay } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BatchInvoiceUploadCategory } from '../../../../../api/gql';
import { InfoCard } from '../../../../../components/InfoCard';
import { XmlData } from '../../../../../models/xml/xmlInvoices';

type XmlSummaryCardsProps = {
  xmlData: XmlData;
  transNs?: string;
};

export const XmlSummaryCards = ({ xmlData, transNs = 'xml-upload' }: XmlSummaryCardsProps) => {
  const { t } = useTranslation(transNs);

  return (
    <Stack flexDirection={'row'} gap={2} flexWrap={'wrap'}>
      {Object.entries(xmlData.invoiceCategoriesSummary)
        .sort(
          (a, b) =>
            CategorySortOrder.indexOf(a[0] as BatchInvoiceUploadCategory) -
            CategorySortOrder.indexOf(b[0] as BatchInvoiceUploadCategory),
        )
        .map(([category, count]) => {
          const categoryData = CategoryDataProps[category as BatchInvoiceUploadCategory];

          return (
            <InfoCard
              key={category}
              sx={{ width: '100%' }}
              icon={categoryData.icon}
              title={t(categoryData.transKey, { count })}
              subtitle={t(`${categoryData.transKey}.subtitle`)}
            />
          );
        })}
    </Stack>
  );
};

const CategorySortOrder = [
  BatchInvoiceUploadCategory.New,
  BatchInvoiceUploadCategory.Ignored,
  BatchInvoiceUploadCategory.Settled,
  BatchInvoiceUploadCategory.PartiallySettled,
  BatchInvoiceUploadCategory.Reminding,
  BatchInvoiceUploadCategory.AlreadyCompleted,
  BatchInvoiceUploadCategory.Archived,
] as const satisfies readonly BatchInvoiceUploadCategory[];

const CategoryDataProps: Record<BatchInvoiceUploadCategory, { icon: ReactNode; transKey: string }> = {
  [BatchInvoiceUploadCategory.New]: {
    icon: <Add fontSize={'large'} color={'info'} />,
    transKey: 'summary.invoicesNew',
  },
  [BatchInvoiceUploadCategory.Ignored]: {
    icon: <Remove fontSize={'large'} color={'action'} />,
    transKey: 'summary.invoicesIgnored',
  },
  [BatchInvoiceUploadCategory.Settled]: {
    icon: <Close fontSize={'large'} color={'error'} />,
    transKey: 'summary.invoicesTerminated',
  },
  [BatchInvoiceUploadCategory.PartiallySettled]: {
    icon: <Replay fontSize={'large'} color={'info'} />,
    transKey: 'summary.invoicesUpdated',
  },
  [BatchInvoiceUploadCategory.Reminding]: {
    icon: <Check fontSize={'large'} color={'success'} />,
    transKey: 'summary.invoicesContinued',
  },
  [BatchInvoiceUploadCategory.AlreadyCompleted]: {
    icon: <DoneAll fontSize={'large'} color={'success'} />,
    transKey: 'summary.invoicesPaid',
  },
  [BatchInvoiceUploadCategory.Archived]: {
    icon: <Delete fontSize={'large'} color={'action'} />,
    transKey: 'summary.invoicesArchived',
  },
};
